import { useEffect, useState } from "react"
import classNames from "classnames"
import dayjs, { Dayjs } from "dayjs"
import styled, { css } from "styled-components"

import { useLazyGetMonitoringHistoricalDataQuery } from "store/api/monitoringApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"

import { fillMonitoringMissingHistoricalData } from "utils/chartUtils"
import { getTimeZoneFullText } from "utils/dateUtils"
import { isMobileDevice } from "utils/deviceDetection"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { Spinner } from "components/common/Spinner"
import TabsWrapper from "components/common/TabsWrapper"
import { Tooltip } from "components/common/Tooltip"
import DateRangeSelect from "components/DateRangeSelect"
import { TemperatureToggle } from "components/form/TemperatureToggle"

import AirQualityChart from "./AirQualityChart"
import { dropDownOptions, getMonitoringDateRangeParams } from "./getMonitoringDateRangeParams"
import HumidityChart from "./HumidityChart"
import SystemModesBarChart from "./SystemModesBarChart"
import TemperatureBarChart from "./TemperatureBarChart"
import TemperatureLineChart from "./TemperatureLineChart"

import styles from "./index.module.scss"

const TimeZoneTextWrapper = styled.span<{ useTabsComponent?: boolean }>`
  display: flex;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin-left: auto;
  justify-content: flex-end;

  ${({ useTabsComponent }) =>
    useTabsComponent &&
    css`
      width: 100%;
      margin-bottom: 20px !important;
    `}
`

const Separator = styled.span`
  position: relative;
  top: 2px;
  font-size: 12px;
  color: #64748b;
  margin: 0 10px;
`

const StyledTooltip = styled(Tooltip)`
  span:after {
    display: inline-block;
    transform: scale(1.4);
    top: 1px;
    margin-left: 6px;
  }
`

const DateRangeContainer = styled.div`
  position: relative;
  display: flex;

  > div {
    position: absolute;
    z-index: 1000;

    @media only screen and (max-width: 768px) {
      position: relative;
      align-self: start;
    }
  }
`

const ChartOptionsContainer = styled.div<{ useTabsComponent?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: ${({ useTabsComponent }) => (useTabsComponent ? "15px" : "40px")};
  margin-bottom: ${({ useTabsComponent }) => (useTabsComponent ? "0" : "15px")};
`

const TabButtonsWrapper = styled.div<{ useTabsComponent?: boolean }>`
  margin-bottom: ${({ useTabsComponent }) => (useTabsComponent ? "0 !important" : "")};
`

interface Props {
  device: ApiTypes.Model.Device
  isCelsius: boolean
  monitoringReportAnnotations?: MonitoringReportAnnotations | null
  onClearMonitoringReportAnnotations?: () => void
  useTabsComponent?: boolean
  defaultPeriod?: string
}

const TabSettings = [
  { name: "Temperature", value: "temperature" },
  { name: "Air Quality", value: "airQuality" },
  { name: "Humidity", value: "humidity" },
]

export const StatsReportChartsWrapper = ({
  device,
  isCelsius,
  monitoringReportAnnotations,
  onClearMonitoringReportAnnotations,
  useTabsComponent = false,
  defaultPeriod,
}: Props) => {
  const userProfile = useAppSelector(state => state.user.userProfile)

  const hasMonitoringAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Monitoring) : false

  const [activeTab, setActiveTab] = useState(TabSettings[0].value)

  const currentTabIndex = TabSettings.findIndex(tab => tab.value === activeTab)

  const handleTabChange = (index: number) => {
    setActiveTab(TabSettings[index].value)
  }

  const getInitialSelectedTime = (): UITypes.Option[] => {
    if (defaultPeriod) {
      const option = dropDownOptions.find(opt => opt.value === defaultPeriod)
      if (option) {
        return [option]
      }
    }

    if (monitoringReportAnnotations) {
      return [dropDownOptions[dropDownOptions.length - 1]]
    }

    return [dropDownOptions[0]]
  }

  const [selectedTime, setSelectedTime] = useState<UITypes.Option[]>(getInitialSelectedTime())
  const timeZone = device.time_zone_text
  const isMobile = isMobileDevice()

  const getInitialSelectedRange: () => [Dayjs, Dayjs] | null = () => {
    if (monitoringReportAnnotations) {
      return [dayjs(monitoringReportAnnotations.start), dayjs(monitoringReportAnnotations.stop)]
    }

    return null
  }

  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs, Dayjs] | null>(getInitialSelectedRange())
  const [isSingleDataPoint, setIsSingleDataPoint] = useState(false)
  const {
    startDateString,
    endDateString,
    startDateTime,
    intervalInMinutes,
    itemsCount,
    settings,
    barChartSettings,
    daysCount = 0,
  } = getMonitoringDateRangeParams(selectedTime[0].value, selectedDateRange, timeZone)
  const [triggerGetMonitoringHistoricalData, { isFetching }] = useLazyGetMonitoringHistoricalDataQuery()
  const [data, setData] = useState<ApiTypes.Res.MonitoringHistoricalDataBucket[] | null>(null)

  useEffect(() => {
    if (!hasMonitoringAccess) {
      setData([])
      setIsSingleDataPoint(false)
      return
    }

    const fetchData = async () => {
      try {
        const res = await triggerGetMonitoringHistoricalData(
          {
            sn: device.sn,
            interval_in_minutes: intervalInMinutes,
            start_date_time: startDateString,
            end_date_time: endDateString,
          },
          true,
        )
        if (res) {
          const data = fillMonitoringMissingHistoricalData(
            res?.data?.aggregations?.["date_histogram#aggregation_over_time"]?.buckets || [],
            startDateTime,
            intervalInMinutes,
            itemsCount,
          )
          setData(data)
          setIsSingleDataPoint(res?.data?.aggregations?.["date_histogram#aggregation_over_time"]?.buckets?.length === 1)
        }
      } catch (error) {
        console.error(error)
        setData([])
        setIsSingleDataPoint(false)
      }
    }

    fetchData()
  }, [device.sn, selectedTime[0].value, selectedDateRange, hasMonitoringAccess])

  const renderCharts = () => {
    if (isFetching || data == null) return <Spinner />

    if (activeTab === "temperature") {
      return (
        <div>
          <TemperatureLineChart
            isCelsius={isCelsius}
            settings={settings}
            historicalData={data}
            isSingleDataPoint={isSingleDataPoint}
            intervalInMinutes={intervalInMinutes}
            monitoringReportAnnotations={monitoringReportAnnotations}
            showSchedule={["lastHour", "last6Hours"].includes(selectedTime[0].value as string) || daysCount === 1}
            timeZone={timeZone}
          />

          {data.length > 0 && (
            <>
              <SystemModesBarChart barChartSettings={barChartSettings} historicalData={data} timeZone={timeZone} />
              <TemperatureBarChart barChartSettings={barChartSettings} historicalData={data} timeZone={timeZone} />
            </>
          )}
        </div>
      )
    }

    if (activeTab === "airQuality") {
      return (
        <AirQualityChart
          timeZone={timeZone}
          settings={settings}
          historicalData={data}
          isSingleDataPoint={isSingleDataPoint}
        />
      )
    }

    return (
      <HumidityChart
        timeZone={timeZone}
        settings={settings}
        historicalData={data}
        isSingleDataPoint={isSingleDataPoint}
        showSetHumidity={device.accessories !== "none"}
      />
    )
  }

  const handleSetSelectedDateRange = (dateRange: [Dayjs, Dayjs] | null) => {
    setSelectedDateRange(dateRange)
    onClearMonitoringReportAnnotations?.()
  }

  const handleSetSelectedTime = (time: UITypes.Option[]) => {
    setSelectedTime(time)
    onClearMonitoringReportAnnotations?.()
  }

  return (
    <div className={classNames(styles.chartsWrapper, { [styles.mobileChartsWrapper]: isMobile })}>
      <ChartOptionsContainer useTabsComponent={useTabsComponent}>
        <TabButtonsWrapper useTabsComponent={useTabsComponent} className={styles.tabButtonsWrapper}>
          {useTabsComponent ? (
            <TabsWrapper
              tabs={TabSettings.map(tab => ({
                id: tab.value,
                label: tab.name,
              }))}
              currentTab={currentTabIndex}
              onTabChange={handleTabChange}
            />
          ) : (
            <div className={styles.tabButtons}>
              {TabSettings.map(tab => (
                <span
                  key={tab.value}
                  className={classNames(styles.tabButton, { [styles.active]: activeTab === tab.value })}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.name}
                </span>
              ))}
            </div>
          )}
          {useTabsComponent ? (
            <TimeZoneTextWrapper useTabsComponent={useTabsComponent}>
              <TemperatureToggle />
              <Separator>|</Separator>
              {getTimeZoneFullText(timeZone)}
              <StyledTooltip id={`time-zone-full-text`} text={""} orientation="bottom-start" variant="dark">
                Data is shown in the thermostat&apos;s local time.
              </StyledTooltip>
            </TimeZoneTextWrapper>
          ) : (
            <TimeZoneTextWrapper useTabsComponent={useTabsComponent}>
              {getTimeZoneFullText(timeZone)}
              <StyledTooltip id={`time-zone-full-text`} text={""} orientation="bottom-start" variant="dark">
                Data is shown in the thermostat&apos;s local time.
              </StyledTooltip>
            </TimeZoneTextWrapper>
          )}
        </TabButtonsWrapper>
      </ChartOptionsContainer>
      <DateRangeContainer>
        <DateRangeSelect
          selectedTime={selectedTime}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={handleSetSelectedDateRange}
          setSelectedTime={handleSetSelectedTime}
          isMobile={isMobile}
        />
      </DateRangeContainer>
      {renderCharts()}
    </div>
  )
}
